import React, { useState, useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import { FileText, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import ModernPagination from '../../ui/ModernPagination.jsx';

const NoteTemplatesTable = ({ templates, isLoading, onEdit, onDelete }) => {
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    // Define columns
    const columns = useMemo(() => [
        {
            accessorKey: 'title',
            header: 'Titel',
            cell: ({ row }) => (
                <div style={{ fontWeight: 500 }}>
                    {row.original.title}
                </div>
            ),
        },
        {
            accessorKey: 'category_name',
            header: 'Kategorie',
            cell: ({ row }) => (
                <span className="badge badge-category">
                    {row.original.category_name}
                </span>
            ),
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <div className="row-actions">
                    <button
                        className="action-btn"
                        onClick={() => onEdit(row.original)}
                        title="Bearbeiten"
                    >
                        <Pencil size={16} />
                    </button>
                    <button
                        className="action-btn delete"
                        onClick={() => onDelete(row.original)}
                        title="Löschen"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ], [onEdit, onDelete]);

    // Create table instance
    const table = useReactTable({
        data: templates,
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    // Loading state
    if (isLoading) {
        return (
            <div className="note-templates-modern">
                <div className="table-container">
                    <div className="loading-overlay">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Laden...</span>
                        </div>
                        <p>Lade Notizvorlagen...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Empty state
    if (templates.length === 0) {
        return (
            <div className="note-templates-modern">
                <div className="table-container">
                    <div className="empty-state">
                        <FileText size={64} />
                        <h3>Keine Notizvorlagen vorhanden</h3>
                        <p>Erstellen Sie Ihre erste Notizvorlage mit dem Button oben.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="note-templates-modern">
            <div className="table-container">
                {/* Table Controls */}
                <div className="table-controls">
                    <div className="table-controls-left">
                        <div className="search-box">
                            <svg className="search-icon" width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Notizvorlagen durchsuchen..."
                                value={globalFilter ?? ''}
                                onChange={e => setGlobalFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary, #64748b)' }}>
                        {table.getFilteredRowModel().rows.length} Einträge
                    </div>
                </div>

                {/* Table */}
                <div className="table-wrapper">
                    <table>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th
                                            key={header.id}
                                            className={header.column.getCanSort() ? 'sortable' : ''}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={header.id === 'actions' ? { width: '100px' } : {}}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() && (
                                                    <span style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                                                        {header.column.getIsSorted() === 'asc' ? (
                                                            <ChevronUp size={14} />
                                                        ) : header.column.getIsSorted() === 'desc' ? (
                                                            <ChevronDown size={14} />
                                                        ) : (
                                                            <span style={{ opacity: 0.3 }}>
                                                                <ChevronUp size={14} style={{ marginBottom: '-4px' }} />
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {table.getPageCount() > 1 && (
                    <div className="pagination">
                        <div className="pagination-info">
                            Zeige {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                            {Math.min(
                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                table.getFilteredRowModel().rows.length
                            )}{' '}
                            von {table.getFilteredRowModel().rows.length}
                        </div>
                        <div className="pagination-controls">
                            <ModernPagination
                                currentPage={table.getState().pagination.pageIndex}
                                totalPages={table.getPageCount()}
                                onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
                                canPreviousPage={table.getCanPreviousPage()}
                                canNextPage={table.getCanNextPage()}
                                onPreviousPage={() => table.previousPage()}
                                onNextPage={() => table.nextPage()}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoteTemplatesTable;
